import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faCheck, faListCheck, faShieldHalved, faTruckFast } from '@fortawesome/free-solid-svg-icons';
import { legacyFrame } from './appStepFrame';
import { FIELD_SX } from '../steps';
import './StepOrders.css';

const API = process.env.REACT_APP_API_BASE_URL;
// The ready-made skill that answers order questions from the Wix store (backend agent_studio/packages/wix.py).
const PACKAGE = 'wix_order_tracking';

const FEATURES = [
  { icon:faBoxOpen, title:'wix_orders_f_status', desc:'wix_orders_f_status_desc' },
  { icon:faTruckFast, title:'wix_orders_f_tracking', desc:'wix_orders_f_tracking_desc' },
  { icon:faListCheck, title:'wix_orders_f_several', desc:'wix_orders_f_several_desc' },
  { icon:faShieldHalved, title:'wix_orders_f_private', desc:'wix_orders_f_private_desc' },
];

// An illustration of the skill's procedure: email, one-time code, then only that address's orders.
const PREVIEW = [
  ['user', 'wix_orders_demo_1'], ['bot', 'wix_orders_demo_2'], ['user', 'wix_orders_demo_3'],
  ['bot', 'wix_orders_demo_4'], ['user', 'wix_orders_demo_5'], ['bot', 'wix_orders_demo_6'],
];

const detail = (data, fallback) => (typeof data?.detail === 'string' ? data.detail : fallback);

export default function StepOrders({ botId, goNext, busy: saving, prefillEmail = '', frame = legacyFrame }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const [installed, setInstalled] = useState(false);
  const [contact, setContact] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      let value = null; let on = false;
      try {
        const response = await fetch(`${API}agent-studio/${encodeURIComponent(botId)}/packages?lang=${lang}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(detail(data, t('wix_orders_load_error')));
        const pkg = (data.packages || []).find(item => item.key === PACKAGE);
        on = Boolean(pkg?.installed);
        value = pkg?.params?.find(p => p.key === 'contact_hint')?.value ?? null;
      } catch (e) { if (alive) setError(e.message); }
      if (!alive) return;
      // A saved contact wins; otherwise suggest the owner's address (saved only on Continue).
      setInstalled(on); setContact(value ?? prefillEmail); setLoading(false);
    })();
    return () => { alive = false; };
  }, [botId, lang, prefillEmail, t]);

  async function switchOn() {
    if (working || saving) return;
    setWorking(true); setError('');
    try {
      const response = await fetch(`${API}agent-studio/${encodeURIComponent(botId)}/packages/${PACKAGE}?lang=${lang}`, {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body:JSON.stringify({ params:{ contact_hint:contact.trim() } }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(detail(data, t('wix_orders_install_error')));
      setInstalled(true);
      goNext();
    } catch (e) { setError(e.message); }
    finally { setWorking(false); }
  }

  return frame({
    title:t('wix_orders_title'), titleClass:'ac-orders-title',
    titleIcon:<span className="ac-orders-title-icon" aria-hidden="true"><FontAwesomeIcon icon={faTruckFast} /></span>,
    sub:t('wix_orders_sub'), bodyClass:'ac-orders-body',
    primary:{ label:t(installed ? 'wxo_continue' : 'wix_orders_switch_on'), onClick:switchOn, disabled:loading || working, busy:saving || working },
    skip:!installed ? { label:t('wix_orders_later'), onClick:goNext } : null,
    children:<>
      <div className="ac-orders-main">
        <ul className="ac-orders-features">
          {FEATURES.map(item => <li key={item.title}>
            <span className="ac-orders-feature-icon" aria-hidden="true"><FontAwesomeIcon icon={item.icon} /></span>
            <span className="ac-orders-feature-copy"><strong>{t(item.title)}</strong><span>{t(item.desc)}</span></span>
          </li>)}
        </ul>
        <div className="ac-orders-contact">
          <TextField size="small" sx={FIELD_SX} label={t('wix_orders_contact_label')} value={contact}
            helperText={t('wix_orders_contact_help')} disabled={loading || working || saving}
            onChange={e => setContact(e.target.value.slice(0, 200))} inputProps={{ autoComplete:'off' }} />
        </div>
        {installed && <div className="ac-orders-on" role="status"><FontAwesomeIcon icon={faCheck} />{t('wix_orders_on')}</div>}
        {error && <div className="ac-error" role="alert">{error}</div>}
      </div>
      <figure className="ac-orders-preview" aria-label={t('wix_orders_preview_label')}>
        <div className="ac-orders-preview-head"><span aria-hidden="true">AI</span>{t('wix_orders_preview_title')}</div>
        <div className="ac-orders-preview-chat">
          {PREVIEW.map(([from, key]) => <p key={key} className={`ac-orders-bubble is-${from}`}>{t(key)}</p>)}
          <span className="ac-orders-track"><FontAwesomeIcon icon={faTruckFast} />{t('wix_orders_demo_link')}</span>
        </div>
        <figcaption>{t('wix_orders_preview_caption')}</figcaption>
      </figure>
    </>,
  });
}
